import type { ApiClient } from './client';
import { dayMonth, dayWord } from './productReports';
import { pageLimit } from './products';

/**
 * Code batches, as `veri-plus-backend` serves them.
 *
 * `routes/v1/product.routes.ts:91-125`, mounted at `/v1/code-batches`, all
 * behind `requireOrgContext`. `POST /` is admin only and takes
 * `issueBatchSchema` (`validation/product.validation.ts:52-63`): a
 * `productId`, a `quantity` of 1 to `CODE_BATCH_MAX_QUANTITY` (10,000 by
 * default), and an optional write-once `provenance`. It answers 201
 * `{ batch }`, or 409 `not_ready_to_issue` with `details` keyed by field
 * (`provenance.batchNumber` and the like) when the codes would carry too
 * little, `readiness.service.ts:123-139`. `GET /` pages newest first and
 * answers `{ batches, nextCursor }`.
 *
 * X3 was ruled by the user: the API makes the file. `GET /:batchId/export`
 * with `format=csv` builds the CSV itself, provenance on every row
 * (`product.controller.ts:112-135`), so the portal fetches the bytes with its
 * bearer and hands them to the browser. It never pages codes or writes CSV.
 * D19.
 */

/** `demo` is a demo org's registered samples, Q5: nothing printed, so nothing to export. */
export type BatchSource = 'issued' | 'ingested' | 'demo';

export interface Provenance {
  manufacturedAt?: string;
  shift?: string;
  productionLine?: string;
  facility?: string;
  batchNumber?: string;
  expiresAt?: string;
}

export interface CodeBatch {
  id: string;
  organizationId: string;
  productId: string;
  source: BatchSource;
  quantity: number;
  provenance?: Provenance;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const codeBatchesPath = '/v1/code-batches';

interface BatchPage {
  batches: CodeBatch[];
  nextCursor: string | null;
}

/** Every page, as D7 walks products: the board draws no pager. A cursor that repeats ends the walk. */
export async function listAllBatches(api: ApiClient, signal?: AbortSignal): Promise<CodeBatch[]> {
  const all: CodeBatch[] = [];
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const query: string = cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`;
    const page: BatchPage = await api.get<BatchPage>(
      `${codeBatchesPath}?limit=${pageLimit}${query}`,
      signal ? { signal } : {},
    );
    all.push(...page.batches);
    cursor = page.nextCursor ?? null;
    if (cursor !== null) {
      if (seen.has(cursor)) break;
      seen.add(cursor);
    }
  } while (cursor !== null);
  return all;
}

export interface IssueInput {
  productId: string;
  /** A number when the field held one, else the text as typed, so the API's own message answers it. */
  quantity: number | string;
  provenance?: Provenance;
}

export async function issueBatch(api: ApiClient, input: IssueInput): Promise<CodeBatch> {
  const answer = await api.post<{ batch: CodeBatch }>(codeBatchesPath, input);
  return answer.batch;
}

/**
 * The four files a batch exports as, D34. The backend's `format=csv|pdf|zip`
 * and, for a PDF only, `page=a4|letter`: the CSV of codes and URLs, the
 * printable sheet, and a ZIP of one SVG and one PNG per code plus the CSV.
 */
export type ExportKind = 'csv' | 'pdf-a4' | 'pdf-letter' | 'zip';

export const exportKinds: readonly ExportKind[] = ['csv', 'pdf-a4', 'pdf-letter', 'zip'];

export const exportLabels: Record<ExportKind, string> = {
  csv: 'CSV',
  'pdf-a4': 'PDF, A4',
  'pdf-letter': 'PDF, Letter',
  zip: 'ZIP of images',
};

const exportQuery: Record<ExportKind, string> = {
  csv: 'format=csv',
  'pdf-a4': 'format=pdf&page=a4',
  'pdf-letter': 'format=pdf&page=letter',
  zip: 'format=zip',
};

const extension: Record<ExportKind, 'csv' | 'pdf' | 'zip'> = {
  csv: 'csv',
  'pdf-a4': 'pdf',
  'pdf-letter': 'pdf',
  zip: 'zip',
};

/** The file's kind as a word for a busy label: CSV, PDF or ZIP. */
export const exportFormat = (kind: ExportKind) => extension[kind].toUpperCase();

/** The API's own file, as bytes. A 10,000-code PDF takes about 11 s. */
export function exportBatch(api: ApiClient, batchId: string, kind: ExportKind): Promise<Blob> {
  return api.get<Blob>(
    `${codeBatchesPath}/${encodeURIComponent(batchId)}/export?${exportQuery[kind]}`,
    { blob: true },
  );
}

/** The name the API gives it in `Content-Disposition`, which CORS does not expose to the page. */
export const exportFileName = (batch: CodeBatch, kind: ExportKind) =>
  `batch-${batch.id}.${extension[kind]}`;

/** The form's six provenance fields, as typed. */
export interface ProvenanceFields {
  batchNumber: string;
  shift: string;
  /** `datetime-local`, the line's own clock. */
  manufacturedAt: string;
  /** `date`. */
  expiresAt: string;
  productionLine: string;
  facility: string;
}

export const emptyProvenance: ProvenanceFields = {
  batchNumber: '',
  shift: '',
  manufacturedAt: '',
  expiresAt: '',
  productionLine: '',
  facility: '',
};

/**
 * What is sent: blank fields left out, and no `provenance` at all when every
 * field is blank. A local date and time becomes an instant; a date is sent as
 * the day, which the API reads as that day at 00:00 UTC.
 */
export function toProvenance(fields: ProvenanceFields): Provenance | undefined {
  const out: Provenance = {};
  for (const key of ['batchNumber', 'shift', 'productionLine', 'facility'] as const) {
    const value = fields[key].trim();
    if (value !== '') out[key] = value;
  }
  if (fields.manufacturedAt !== '') {
    const at = new Date(fields.manufacturedAt);
    out.manufacturedAt = Number.isNaN(at.getTime()) ? fields.manufacturedAt : at.toISOString();
  }
  if (fields.expiresAt !== '') out.expiresAt = fields.expiresAt;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** `10,000` and `12,000` are drawn: digits with separators allowed, else the text for the API to refuse. */
export function toQuantity(text: string): number | string {
  const bare = text.replace(/[,\s]/g, '');
  return /^\d+$/.test(bare) ? Number(bare) : text.trim();
}

/** The Batch column: `#B-1042`, the pack's own batch number. No number, no label, as D11's picker. */
export const batchCell = (batch: CodeBatch) =>
  batch.provenance?.batchNumber ? `#${batch.provenance.batchNumber}` : '';

/** `10,000`, `800`. */
export const quantityCell = (batch: CodeBatch) => batch.quantity.toLocaleString('en-US');

export const sourceLabels: Record<BatchSource, string> = {
  issued: 'Issued',
  ingested: 'Ingested',
  demo: 'Demo',
};

/** The newest few, one page: the dashboard's Recent code batches. */
export async function recentBatches(
  api: ApiClient,
  limit: number,
  signal?: AbortSignal,
): Promise<CodeBatch[]> {
  const page = await api.get<BatchPage>(
    `${codeBatchesPath}?limit=${limit}`,
    signal ? { signal } : {},
  );
  return page.batches;
}

/** `#B-1042 · Premium Lager`, or the product alone when the pack carries no number. */
export const batchTitle = (batch: CodeBatch, product: string | undefined) =>
  [batchCell(batch), product ?? ''].filter((part) => part !== '').join(' · ');

/**
 * `2 days ago`, `1 week ago` are drawn. `today` and `yesterday` are the
 * inbox's words; past five weeks the date, `8 Sep`.
 */
export function batchAgo(iso: string, now: Date): string {
  const at = new Date(iso);
  const word = dayWord(at, now);
  if (word !== null) return word;
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(now) - start(at)) / 86_400_000);
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  return dayMonth(at);
}

/** `10,000 · issued · 2 days ago`. */
export const batchLine = (batch: CodeBatch, now: Date) =>
  `${quantityCell(batch)} · ${batch.source} · ${batchAgo(batch.createdAt, now)}`;
